import axios, { AxiosHeaders, type AxiosError, type InternalAxiosRequestConfig } from "axios";
import type { ApiErrorEnvelope, ApiErrorIssues } from "@ibada/shared-types/envelopes";
import { LOCALE_HEADER } from "@ibada/shared-types/i18n";

import { getActiveLocale } from "@/i18n/active-locale";

import { resolveTenantRouteContext } from "@/shared/lib/branch/branch-admin-routing";
import { captureApiRequestPerf } from "@/shared/lib/analytics/analytics";

type ErrorPayload = Partial<Pick<ApiErrorEnvelope, "code" | "message">> & {
  issues?: Partial<ApiErrorIssues>;
};

const baseURL = (import.meta.env.VITE_API_BASE_URL ?? "http://localhost:5050").replace(/\/$/, "");

/**
 * Build a local ISO 8601 timestamp with the user's timezone offset
 * baked in (e.g. `2026-06-04T14:56:00.000+01:00`). Native
 * `toISOString()` always returns UTC, which loses the wall-clock
 * information backends need for "Generated on" / "Submitted at"
 * timestamps.
 */
function formatLocalIsoWithOffset(date: Date): string {
  const pad = (n: number) => n.toString().padStart(2, "0");
  const yyyy = date.getFullYear();
  const mm = pad(date.getMonth() + 1);
  const dd = pad(date.getDate());
  const hh = pad(date.getHours());
  const mins = pad(date.getMinutes());
  const ss = pad(date.getSeconds());
  const ms = date.getMilliseconds().toString().padStart(3, "0");
  const totalEastMinutes = -date.getTimezoneOffset();
  const sign = totalEastMinutes >= 0 ? "+" : "-";
  const offAbs = Math.abs(totalEastMinutes);
  const offHh = pad(Math.floor(offAbs / 60));
  const offMm = pad(offAbs % 60);
  return `${yyyy}-${mm}-${dd}T${hh}:${mins}:${ss}.${ms}${sign}${offHh}:${offMm}`;
}

type AuthTokenGetter = (options?: { skipCache?: boolean }) => Promise<string | null> | string | null;
type AuthAwareRequestConfig = InternalAxiosRequestConfig & {
  _retryWithFreshToken?: boolean;
  metadata?: { perfStartedAt: number };
};
type HttpUnauthorizedHandler = () => Promise<void> | void;
type HttpForbiddenHandler = () => Promise<boolean> | boolean;

let authTokenGetter: AuthTokenGetter | null = null;
let refreshTokenPromise: Promise<string | null> | null = null;
let unauthorizedHandler: HttpUnauthorizedHandler | null = null;
let unauthorizedHandlingPromise: Promise<void> | null = null;
let forbiddenHandler: HttpForbiddenHandler | null = null;
let forbiddenHandlingPromise: Promise<boolean> | null = null;

export function setHttpAuthTokenGetter(getter: AuthTokenGetter | null): void {
  authTokenGetter = getter;
}

export function setHttpUnauthorizedHandler(handler: HttpUnauthorizedHandler | null): void {
  unauthorizedHandler = handler;
}

export function setHttpForbiddenHandler(handler: HttpForbiddenHandler | null): void {
  forbiddenHandler = handler;
}

export class ApiError extends Error {
  status: number;
  code?: string;
  issues?: ErrorPayload["issues"];

  constructor(message: string, status: number, issues?: ErrorPayload["issues"], code?: string) {
    super(message);
    this.name = "ApiError";
    this.status = status;
    this.issues = issues;
    this.code = code;
  }
}

export function isApiForbiddenError(error: unknown): error is ApiError {
  return error instanceof ApiError && error.status === 403;
}

function fallbackMessageFromStatus(status: number): string {
  if (status === 401) return "Unauthenticated. Please sign in again.";
  if (status === 403) return "You do not have permission to perform this action.";
  if (status === 404) return "The requested resource was not found.";
  if (status === 409) return "A conflicting pending invitation already exists.";
  if (status === 410) return "Invitation expired.";
  if (status === 502) return "Invitation delivery failed. Please try again.";
  return "Request failed.";
}

function extractErrorMessage(payload: ErrorPayload | undefined, status: number, fallback: string): string {
  const base = typeof payload?.message === "string" && payload.message.trim().length > 0
    ? payload.message.trim()
    : fallbackMessageFromStatus(status) || fallback;
  const formErrors = Array.isArray(payload?.issues?.formErrors)
    ? payload?.issues?.formErrors.filter((value) => typeof value === "string" && value.trim().length > 0)
    : [];

  return sanitizeUserFacingMessage(formErrors.length > 0 ? `${base} ${formErrors.join(" ")}`.trim() : base);
}

function sanitizeUserFacingMessage(message: string): string {
  if (!message.trim()) return message;

  return message
    .replace(/\bclerk\b/gi, "authentication service")
    .replace(/\bprovider\b/gi, "service");
}

async function handleUnauthorized(): Promise<void> {
  if (!unauthorizedHandler) return;
  if (unauthorizedHandlingPromise) return unauthorizedHandlingPromise;

  unauthorizedHandlingPromise = Promise.resolve(unauthorizedHandler()).finally(() => {
    unauthorizedHandlingPromise = null;
  });

  return unauthorizedHandlingPromise;
}

async function handleForbidden(): Promise<boolean> {
  if (!forbiddenHandler) return false;
  if (forbiddenHandlingPromise) return forbiddenHandlingPromise;

  forbiddenHandlingPromise = Promise.resolve(forbiddenHandler()).finally(() => {
    forbiddenHandlingPromise = null;
  });

  return forbiddenHandlingPromise;
}

async function resolveAuthToken(options?: { skipCache?: boolean }): Promise<string | null> {
  if (authTokenGetter) {
    try {
      const token = await authTokenGetter(options);
      if (token) {
        return token;
      }
    } catch {
      /* fall through to the Clerk window fallback below */
    }
  }

  if (typeof window !== "undefined") {
    const clerk = (
      window as Window & {
        Clerk?: { session?: { getToken?: (opts?: { skipCache?: boolean }) => Promise<string | null> } };
      }
    ).Clerk;
    try {
      return (await clerk?.session?.getToken?.(options)) ?? null;
    } catch {
      return null;
    }
  }

  return null;
}

async function refreshAuthToken(): Promise<string | null> {
  if (refreshTokenPromise) {
    return refreshTokenPromise;
  }

  refreshTokenPromise = (async () => {
    try {
      const freshToken = await resolveAuthToken({ skipCache: true });
      if (freshToken) return freshToken;
      return await resolveAuthToken();
    } catch {
      return null;
    }
  })().finally(() => {
    refreshTokenPromise = null;
  });

  return refreshTokenPromise;
}

function resolveTenantSlugsFromLocation() {
  if (typeof window === "undefined") return null;
  const { workspaceSlug, branchSlug } = resolveTenantRouteContext(window.location.pathname);
  if (!workspaceSlug) return null;
  return { workspaceSlug, branchSlug };
}

/**
 * The auth + tenant headers `http` sends, for requests that cannot go through axios — a streamed chat
 * response is read with `fetch`. Same token, same workspace/branch, so the API resolves the same context.
 */
export async function buildApiRequestHeaders(): Promise<Record<string, string>> {
  const headers: Record<string, string> = {
    "X-Client-Time": formatLocalIsoWithOffset(new Date()),
    [LOCALE_HEADER]: getActiveLocale(),
  };
  const token = (await resolveAuthToken().catch(() => null)) ?? (await refreshAuthToken());
  if (token) headers.Authorization = `Bearer ${token}`;
  const tenantSlugs = resolveTenantSlugsFromLocation();
  if (tenantSlugs) {
    headers["x-workspace-slug"] = tenantSlugs.workspaceSlug;
    if (tenantSlugs.branchSlug) headers["x-branch-slug"] = tenantSlugs.branchSlug;
  }
  return headers;
}

export const apiBaseUrl = baseURL;

export const http = axios.create({
  baseURL,
  headers: {
    "Content-Type": "application/json",
  },
});

http.interceptors.request.use(async (config) => {
  let token: string | null = null;
  try {
    token = (await resolveAuthToken()) ?? (await refreshAuthToken());
  } catch {
    /* unauthenticated request — proceed without a token */
  }
  const headers = AxiosHeaders.from(config.headers);

  if (token) {
    headers.set("Authorization", `Bearer ${token}`);
  } else {
    headers.delete("Authorization");
  }

  // Tenant context, sent on EVERY request (like Authorization) whenever the active URL is
  // workspace-scoped. Branch context is added only when the current route has a branch segment.
  // Resolution returns null off tenant routes, so non-tenant calls are simply left unchanged.
  const tenantSlugs = resolveTenantSlugsFromLocation();
  if (tenantSlugs) {
    headers.set("x-workspace-slug", tenantSlugs.workspaceSlug);
    if (tenantSlugs.branchSlug) {
      headers.set("x-branch-slug", tenantSlugs.branchSlug);
    }
  }

  // Caller-clock context — sent on every request so any backend can
  // render documents, audit logs or notifications in the user's
  // wall-clock time without each call site re-implementing this.
  //
  // Single header carries both the instant AND the timezone offset
  // (ISO 8601, e.g. `2026-06-04T14:56:00.000+01:00`). The backend
  // parses the trailing offset to know the user's local clock.
  if (!headers.has("X-Client-Time")) {
    headers.set("X-Client-Time", formatLocalIsoWithOffset(new Date()));
  }
  // ADR-0030: the current UI language. The API returns every seeded name (chart of accounts, linked funds)
  // already localized in it, so the app never translates those names itself.
  headers.set(LOCALE_HEADER, getActiveLocale());
  // Default axios Content-Type is application/json; multipart uploads must omit it so the
  // runtime sets multipart/form-data with the correct boundary (otherwise the server sees `{}`).
  const isFormData =
    (typeof FormData !== "undefined" && config.data instanceof FormData) ||
    (config.data &&
      typeof config.data === "object" &&
      (config.data as { constructor?: { name?: string } }).constructor?.name === "FormData");
  if (isFormData) {
    headers.delete("Content-Type");
  }
  config.headers = headers;
  // Stamp just before send (after token resolution) so client-observed latency reflects network + backend,
  // not our own token work. Read back in the perf response interceptor below.
  (config as AuthAwareRequestConfig).metadata = { perfStartedAt: performance.now() };
  return config;
});

// Client-observed API latency → PostHog (production-only). Separate from the auth interceptor so it never
// interferes with the 401 refresh/retry flow; captures on both success and failure.
function recordRequestPerf(config: AuthAwareRequestConfig | undefined, status: number): void {
  const startedAt = config?.metadata?.perfStartedAt;
  if (typeof startedAt !== "number" || !config?.url) return;
  captureApiRequestPerf({
    method: config.method ?? "get",
    url: config.url,
    status,
    durationMs: performance.now() - startedAt,
  });
}

http.interceptors.response.use(
  (response) => {
    recordRequestPerf(response.config as AuthAwareRequestConfig, response.status);
    return response;
  },
  (error: AxiosError) => {
    recordRequestPerf(error.config as AuthAwareRequestConfig | undefined, error.response?.status ?? 0);
    return Promise.reject(error);
  },
);

http.interceptors.response.use(
  (response) => response,
  async (error: AxiosError<ErrorPayload>) => {
    const status = error.response?.status ?? 0;
    const payload = error.response?.data;

    const originalRequest = error.config as AuthAwareRequestConfig | undefined;
    if (originalRequest && status === 401 && !originalRequest._retryWithFreshToken) {
      originalRequest._retryWithFreshToken = true;

      const refreshedToken = await refreshAuthToken();
      if (refreshedToken) {
        const retryHeaders = AxiosHeaders.from(originalRequest.headers);
        retryHeaders.set("Authorization", `Bearer ${refreshedToken}`);
        originalRequest.headers = retryHeaders;
        return http(originalRequest);
      }
    }

    if (originalRequest && status === 401) {
      await handleUnauthorized();
    }

    if (status === 403) {
      await handleForbidden();
    }

    const message = extractErrorMessage(payload, status, error.message);
    return Promise.reject(new ApiError(message, status, payload?.issues, payload?.code));
  },
);
